import { redirectToLogin } from "@src/appV2/Auth/redirectToLogin";
import { getClockOffsetMs } from "@src/appV2/lib/clockOffset";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";

import { isTestNodeEnvironment } from "../environment";
import { APP_V2_APP_EVENTS, logEvent } from "../lib/analytics";

/**
 * Response-interceptor error handler. Catches 401s centrally and bounces
 * the user to /login. `redirectToLogin` is idempotent and no-ops on login
 * routes, so concurrent failing requests don't loop.
 *
 * Requests whose config has `skipAuthRedirectOnUnauthorized = true` are
 * exempt — those are shortlink/QR-token flows on unauthenticated pages
 * (set by the request interceptor when the caller pre-supplies the
 * `Authorization` header). A 401 there means the caller's token is bad,
 * not that the user's session has expired, so the page should surface its
 * own error instead of getting bounced to /login.
 *
 * The 401 path returns a never-resolving promise rather than re-throwing.
 * Throwing would propagate the rejection to callers — useQuery onError,
 * `meta.logErrorMessage`, ad-hoc `.catch()` handlers — and a generic error
 * toast would flash in the brief window before the browser navigates to
 * /login. Resolving with undefined would crash callers reading
 * `response.data` in that same window. Holding the promise pending keeps
 * the caller in its loading state until React tears down on navigation.
 */
export async function handleApiResponseError(error: unknown): Promise<void> {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    !error.config?.skipAuthRedirectOnUnauthorized
  ) {
    emit401Diagnostic(error);
    redirectToLogin("apiClient");
    return new Promise<never>(() => {
      // Intentionally never resolves — see comment above.
    });
  }
  if (error instanceof Error) {
    throw error;
  }
}

/**
 * Daily-logout investigation only — splits the 401 redirect cohort into
 * the three mechanisms hypothesized today:
 *
 *   1. Empty token (interceptor's `if (cognitoToken)` was false)
 *      → `hadAuthHeader: false`
 *   2. Frontend-expired token sent (Amplify cached past `exp`, no refresh)
 *      → `hadAuthHeader: true`, `tokenExpSecondsRemaining` ≤ 0
 *   3. Backend-rejected fresh-looking token (clock skew, revoked family,
 *      backend validation regression)
 *      → `hadAuthHeader: true`, `tokenExpSecondsRemaining` > 0
 *
 * Remove this once the dominant mechanism is identified and the real fix
 * has soaked — the matching draft revert PR is the cleanup vehicle.
 */
function emit401Diagnostic(error: AxiosError): void {
  if (isTestNodeEnvironment()) {
    return;
  }

  const { config } = error;
  const authHeader = readAuthHeader(config);
  const decoded = authHeader ? decodeJwtClaims(authHeader) : undefined;
  const nowSeconds = Math.floor(Date.now() / 1000);

  const responseBody = readResponseBody(error.response?.data);

  logEvent(APP_V2_APP_EVENTS.COGNITO_401_DIAGNOSTIC, {
    hadAuthHeader: Boolean(authHeader),
    tokenIatSecondsAgo: typeof decoded?.iat === "number" ? nowSeconds - decoded.iat : null,
    tokenExpSecondsRemaining: typeof decoded?.exp === "number" ? decoded.exp - nowSeconds : null,
    clockOffsetMs: getClockOffsetMs(),
    method: config?.method,
    urlPath: extractPath(config?.url),
    responseBody,
  });
}

function readAuthHeader(config: AxiosRequestConfig | undefined): string | undefined {
  const raw = config?.headers?.Authorization ?? config?.headers?.authorization;
  if (typeof raw !== "string") {
    return undefined;
  }
  return raw.startsWith("Bearer ") ? raw.slice("Bearer ".length) : raw;
}

interface JwtClaims {
  iat?: number;
  exp?: number;
}

function decodeJwtClaims(token: string): JwtClaims | undefined {
  const parts = token.split(".");
  if (parts.length < 2) {
    return undefined;
  }
  try {
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return undefined;
  }
}

const MAX_RESPONSE_BODY_CHARS = 200;

function readResponseBody(data: unknown): string | undefined {
  if (data === undefined || data === null) {
    return undefined;
  }
  const stringified = typeof data === "string" ? data : safeJsonStringify(data);
  if (stringified === undefined) {
    return undefined;
  }
  return stringified.length > MAX_RESPONSE_BODY_CHARS
    ? stringified.slice(0, MAX_RESPONSE_BODY_CHARS)
    : stringified;
}

function safeJsonStringify(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function extractPath(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url, "http://placeholder").pathname;
  } catch {
    return url.split("?")[0];
  }
}
